import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material'
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  NotificationsActive as ReminderIcon,
} from '@mui/icons-material'
import { scheduleService, GroupSchedule } from '../services/scheduleService'
import TestScheduleModal from '../components/schedule/TestScheduleModal'
import { SearchField } from '../components/common/SearchField'

const TestSchedulePage: React.FC = () => {
  const { t, i18n } = useTranslation('test')
  const navigate = useNavigate()
  const [schedules, setSchedules] = useState<GroupSchedule[]>([])
  const [filteredSchedules, setFilteredSchedules] = useState<GroupSchedule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedSchedule, setSelectedSchedule] = useState<GroupSchedule | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [reminderLoading, setReminderLoading] = useState(false)

  const loadSchedules = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await scheduleService.getGroupSchedules()
      setSchedules(response.groupSchedules)
    } catch (err) {
      setError(t('schedule.errors.loadFailed'))
      console.error('Schedule loading error:', err)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadSchedules()
  }, [loadSchedules])

  useEffect(() => {
    let filtered = schedules

    // 検索フィルター
    if (search) {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter(
        (s) =>
          s.test.title.toLowerCase().includes(searchLower) ||
          s.userGroup.name.toLowerCase().includes(searchLower) ||
          s.test.complianceRule.category.toLowerCase().includes(searchLower)
      )
    }

    // ステータスフィルター
    if (statusFilter !== 'all') {
      filtered = filtered.filter((s) => s.status === statusFilter)
    }

    setFilteredSchedules(filtered)
  }, [schedules, search, statusFilter])

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, schedule: GroupSchedule) => {
    setAnchorEl(event.currentTarget)
    setSelectedSchedule(schedule)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedSchedule(null)
  }

  const handleDelete = async () => {
    if (!selectedSchedule) return

    try {
      setDeleteLoading(true)
      await scheduleService.deleteGroupSchedule(selectedSchedule.id)
      await loadSchedules()
      handleMenuClose()
    } catch (err) {
      setError(t('schedule.errors.deleteFailed'))
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleReminder = async () => {
    if (!selectedSchedule) return

    try {
      setReminderLoading(true)
      setError(null)
      const result = await scheduleService.sendGroupScheduleReminder(selectedSchedule.id)
      setSuccessMessage(result.message)
      handleMenuClose()
    } catch (err) {
      setError(t('schedule.errors.reminderFailed'))
    } finally {
      setReminderLoading(false)
    }
  }

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Chip
            icon={<PlayIcon />}
            label={t('schedule.status.active')}
            color="primary"
            size="small"
          />
        )
      case 'upcoming':
        return (
          <Chip
            icon={<AccessTimeIcon />}
            label={t('schedule.status.upcoming')}
            color="secondary"
            size="small"
          />
        )
      case 'ended':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label={t('schedule.status.ended')}
            color="default"
            size="small"
          />
        )
      case 'inactive':
        return (
          <Chip
            icon={<WarningIcon />}
            label={t('schedule.status.inactive')}
            color="secondary"
            size="small"
          />
        )
      default:
        return <Chip label={t('schedule.status.notSet')} size="small" />
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US'
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const getProgressColor = (rate: number) => {
    if (rate >= 80) return 'primary'
    if (rate >= 50) return 'secondary'
    return 'secondary'
  }

  return (
    <Box>
      {/* ヘッダー */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('schedule.title')}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
          {t('schedule.createNew')}
        </Button>
      </Box>

      {/* フィルター */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={t('schedule.searchPlaceholder')}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('schedule.columns.status')}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label={t('schedule.columns.status')}
            >
              <MenuItem value="all">{t('schedule.status.all')}</MenuItem>
              <MenuItem value="active">{t('schedule.status.active')}</MenuItem>
              <MenuItem value="upcoming">{t('schedule.status.upcoming')}</MenuItem>
              <MenuItem value="ended">{t('schedule.status.ended')}</MenuItem>
              <MenuItem value="inactive">{t('schedule.status.inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 成功メッセージ表示 */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* メインコンテンツ */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredSchedules.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <ScheduleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {search || statusFilter !== 'all'
              ? t('schedule.list.noMatchingSchedules')
              : t('schedule.list.noSchedules')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('schedule.list.createNewMessage')}
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
            {t('schedule.createNew')}
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>{t('schedule.columns.test')}</TableCell>
                <TableCell>{t('schedule.columns.targetGroup')}</TableCell>
                <TableCell>{t('schedule.columns.period')}</TableCell>
                <TableCell>{t('schedule.columns.status')}</TableCell>
                <TableCell>{t('schedule.columns.progress')}</TableCell>
                <TableCell>{t('schedule.columns.details')}</TableCell>
                <TableCell align="right">{t('schedule.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSchedules.map((schedule) => (
                <TableRow key={schedule.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {schedule.test.title}
                      </Typography>
                      <Chip
                        size="small"
                        label={schedule.test.complianceRule.category}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2">{schedule.userGroup.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {schedule.userGroup._count?.members || 0}
                          {i18n.language === 'ja' ? '名' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(schedule.startDate)} {i18n.language === 'ja' ? '〜' : '-'}{' '}
                      {formatDate(schedule.endDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(schedule.status)}</TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 120 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">
                          {schedule.progressInfo.completedCount}/{schedule.progressInfo.totalCount}
                          {i18n.language === 'ja' ? '名' : ''}
                        </Typography>
                        <Typography variant="caption" fontWeight="bold">
                          {schedule.progressInfo.progressRate}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={schedule.progressInfo.progressRate}
                        color={getProgressColor(schedule.progressInfo.progressRate)}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {schedule.progressInfo.statusBreakdown.ASSIGNED > 0 && (
                          <Chip
                            size="small"
                            label={`${t('schedule.progressInfo.notStarted')}: ${schedule.progressInfo.statusBreakdown.ASSIGNED}`}
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {schedule.progressInfo.statusBreakdown.IN_PROGRESS > 0 && (
                          <Chip
                            size="small"
                            label={`${t('schedule.progressInfo.inProgress')}: ${schedule.progressInfo.statusBreakdown.IN_PROGRESS}`}
                            color="secondary"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {schedule.progressInfo.statusBreakdown.COMPLETED > 0 && (
                          <Chip
                            size="small"
                            label={`${t('schedule.progressInfo.completedStatus')}: ${schedule.progressInfo.statusBreakdown.COMPLETED}`}
                            color="primary"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {schedule.progressInfo.statusBreakdown.OVERDUE > 0 && (
                          <Chip
                            size="small"
                            label={`${t('schedule.progressInfo.overdue')}: ${schedule.progressInfo.statusBreakdown.OVERDUE}`}
                            color="secondary"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t('schedule.actions.viewDetails')}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/test-schedules/${schedule.id}`)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, schedule)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 統計サマリー */}
      {!loading && schedules.length > 0 && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Stack direction="row" spacing={4} justifyContent="center">
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {schedules.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('schedule.summary.total')}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {schedules.filter((s) => s.status === 'active').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('schedule.summary.active')}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="secondary.main" fontWeight="bold">
                {schedules.filter((s) => s.status === 'upcoming').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('schedule.summary.upcoming')}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="text.secondary" fontWeight="bold">
                {schedules.filter((s) => s.status === 'ended').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('schedule.summary.ended')}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* アクションメニュー */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (selectedSchedule) navigate(`/test-schedules/${selectedSchedule.id}`)
            handleMenuClose()
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('schedule.actions.viewDetails')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedSchedule) navigate(`/test-schedules/${selectedSchedule.id}?edit=true`)
            handleMenuClose()
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('schedule.actions.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleReminder} disabled={reminderLoading}>
          <ListItemIcon>
            {reminderLoading ? (
              <CircularProgress size={20} />
            ) : (
              <ReminderIcon fontSize="small" color="secondary" />
            )}
          </ListItemIcon>
          <ListItemText sx={{ color: 'secondary.main' }}>
            {t('schedule.actions.reminder')}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} disabled={deleteLoading}>
          <ListItemIcon>
            {deleteLoading ? (
              <CircularProgress size={20} />
            ) : (
              <DeleteIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            )}
          </ListItemIcon>
          <ListItemText sx={{ color: 'text.secondary' }}>
            {t('schedule.actions.delete')}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* 新規作成モーダル */}
      <TestScheduleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          setModalOpen(false)
          loadSchedules()
        }}
      />
    </Box>
  )
}

export default TestSchedulePage
